// src/components/inspections/InspectionsView.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../contexts/authClient'; // ✅ instance partagée avec intercepteur CSRF
import {
  ClipboardList, CheckCircle, AlertCircle, Clock, Calendar,
  Plus, Search, Filter, Download, FileText, FileSpreadsheet,
  ChevronDown, ChevronRight, Eye, Edit2, Trash2, X,
  Loader, RefreshCw, Plane, Fuel, Wrench, Activity
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

// ─── Stat Card Component ──────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color, sub }) => (
  <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl p-4">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-xs font-medium uppercase tracking-wider">{title}</p>
        <p className="text-2xl font-bold text-white mt-1" style={{ color }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </p>
        {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
      </div>
      <div className="p-2 rounded-lg" style={{ background: `${color}20` }}>
        <Icon size={18} style={{ color }} />
      </div>
    </div>
  </div>
);

// ─── Main Component ──────────────────────────────────────────────────────────
const InspectionsView = () => {
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [dateRange, setDateRange] = useState('30d');
  const [expandedId, setExpandedId] = useState(null);
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  
  // États pour le modal de création
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [zones, setZones] = useState([]);
  const [staff, setStaff] = useState([]);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    template_id: '',
    zone_id: '',
    scheduled_date: new Date().toISOString().slice(0, 10),
    assigned_to: ''
  });

  // Stats
  const [stats, setStats] = useState({
    scheduled: 0,
    completed: 0,
    overdue: 0,
    passRate: 0
  });

  // Fetch company settings
  const fetchCompanySettings = useCallback(async () => {
    try {
      const response = await api.get('/company/settings');
      if (response.data) {
        setCompany(response.data);
        if (response.data.logo_url) {
          setLogoUrl(`${API_BASE}${response.data.logo_url}`);
        }
      }
    } catch (error) {
      console.error('Error fetching company settings:', error);
    }
  }, []);

  // Fetch inspections
  const fetchInspections = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        range: dateRange,
        status: statusFilter !== 'all' ? statusFilter : undefined
      };
      const response = await api.get('/inspections/', { params });
      const data = response.data || [];
      setInspections(data);
      
      const scheduled = data.filter(i => i.status === 'scheduled').length;
      const completed = data.filter(i => i.status === 'completed').length;
      const overdue = data.filter(i => i.status === 'overdue').length;
      const passed = data.filter(i => i.overall_result === 'pass').length;
      const passRate = completed > 0 ? (passed / completed) * 100 : 0;
      
      setStats({ scheduled, completed, overdue, passRate: passRate.toFixed(1) });
    } catch (error) {
      console.error('Error fetching inspections:', error);
      setInspections([]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, [dateRange, statusFilter]);

  // Charger les données pour le formulaire
  const loadFormData = useCallback(async () => {
    try {
      const [templatesRes, zonesRes, staffRes] = await Promise.all([
        api.get('/inspections/templates'),
        api.get('/airport/zones/'),
        api.get('/staff/')
      ]);
      setTemplates(templatesRes.data || []);
      setZones(zonesRes.data || []);
      setStaff(staffRes.data || []);
    } catch (error) {
      console.error('Error loading form data:', error);
    }
  }, []);

  // Créer une inspection
  const handleCreateInspection = async () => {
    if (!createForm.template_id || !createForm.scheduled_date) {
      alert('Template and scheduled date are required');
      return;
    }
    
    setCreating(true);
    try {
      await api.post('/inspections/', createForm); // ✅ intercepteur CSRF actif
      await fetchInspections();
      setShowCreateModal(false);
      setCreateForm({
        template_id: '',
        zone_id: '',
        scheduled_date: new Date().toISOString().slice(0, 10),
        assigned_to: ''
      });
    } catch (error) {
      console.error('Error creating inspection:', error);
      alert(error.response?.data?.detail || 'Error creating inspection');
    } finally {
      setCreating(false);
    }
  };

  const openCreateModal = () => {
    loadFormData();
    setShowCreateModal(true);
  };

  useEffect(() => {
    fetchCompanySettings();
    fetchInspections();
  }, [fetchInspections, fetchCompanySettings]);

  const filteredInspections = inspections.filter(insp => {
    const matchesSearch = 
      insp.inspection_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insp.template_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      insp.zone_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || insp.category === typeFilter;
    return matchesSearch && matchesType;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return 'text-green-400 bg-green-500/20';
      case 'scheduled': return 'text-blue-400 bg-blue-500/20';
      case 'in_progress': return 'text-yellow-400 bg-yellow-500/20';
      case 'overdue': return 'text-red-400 bg-red-500/20';
      case 'needs_review': return 'text-orange-400 bg-orange-500/20';
      default: return 'text-gray-400 bg-gray-500/20';
    }
  };

  const getResultColor = (result) => {
    switch(result) {
      case 'pass': return 'text-green-400';
      case 'fail': return 'text-red-400';
      case 'warning': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  const exportToExcel = () => {
    const exportData = filteredInspections.map(insp => ({
      'Inspection #': insp.inspection_number,
      'Template': insp.template_name,
      'Zone': insp.zone_name || insp.zone_type,
      'Category': insp.category,
      'Scheduled Date': insp.scheduled_date,
      'Status': insp.status,
      'Result': insp.overall_result || 'N/A',
      'Performed By': insp.performed_by_name || 'N/A',
      'Performed Date': insp.performed_date || 'N/A'
    }));
    
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inspections');
    XLSX.writeFile(wb, `inspections_${new Date().toISOString().slice(0,19)}.xlsx`);
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const companyName = company?.name || 'AeroTrick FBO';
    
    if (logoUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.src = logoUrl;
        await new Promise((resolve) => {
          img.onload = () => {
            doc.addImage(img, 'PNG', 14, 10, 40, 15);
            resolve();
          };
          img.onerror = resolve;
        });
      } catch (e) { console.warn('Logo not loaded'); }
    }
    
    doc.setFontSize(18);
    doc.setTextColor(30, 64, 175);
    doc.text('Inspections Report', 70, 20);
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Scheduled: ${stats.scheduled}`, 14, 45);
    doc.text(`Completed: ${stats.completed}`, 80, 45);
    doc.text(`Overdue: ${stats.overdue}`, 150, 45);
    doc.text(`Pass Rate: ${stats.passRate}%`, 14, 55);
    
    const tableData = filteredInspections.slice(0, 50).map(insp => [
      insp.inspection_number,
      insp.template_name?.slice(0, 25) || '',
      insp.scheduled_date,
      insp.status,
      insp.overall_result || 'N/A'
    ]);
    
    autoTable(doc, {
      startY: 65,
      head: [['Inspection #', 'Template', 'Date', 'Status', 'Result']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246] },
      margin: { left: 14, right: 14 }
    });
    
    const finalY = doc.lastAutoTable?.finalY || 250;
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175);
    doc.text(
      `Generated by AeroTrick for ${companyName} · © 2026 AeroTrick. All rights reserved.`,
      14,
      finalY + 20
    );
    
    doc.save(`inspections_${new Date().toISOString().slice(0,19)}.pdf`);
  };

  return (
    <div className="space-y-4 px-1">
      
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {logoUrl && (
            <img 
              src={logoUrl} 
              alt="Company Logo" 
              className="h-10 w-auto object-contain"
              onError={(e) => e.target.style.display = 'none'}
            />
          )}
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <div className="p-1.5 bg-blue-500/20 rounded-lg">
                <ClipboardList size={18} className="text-blue-400" />
              </div>
              Inspections Management
            </h2>
            <p className="text-gray-400 text-xs mt-0.5 flex items-center gap-1">
              <Clock size={11} />
              Last refresh: {lastRefresh.toLocaleTimeString()} — FAA 150/5200-18C Compliant
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-gray-800 border border-gray-700 rounded-lg overflow-hidden text-xs">
            {['7d', '30d', '90d'].map(r => (
              <button key={r} onClick={() => setDateRange(r)}
                className={`px-3 py-1.5 transition-colors ${dateRange === r ? 'bg-blue-600 text-white' : 'text-gray-400 hover:text-white'}`}>
                {r === '7d' ? '7 Days' : r === '30d' ? '30 Days' : '3 Months'}
              </button>
            ))}
          </div>
          
          <button onClick={exportToExcel}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600/20 hover:bg-green-600/30 border border-green-500/30 rounded-lg text-green-400 text-xs">
            <FileSpreadsheet size={12} /> Excel
          </button>
          <button onClick={exportToPDF}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-600/20 hover:bg-red-600/30 border border-red-500/30 rounded-lg text-red-400 text-xs">
            <FileText size={12} /> PDF
          </button>
          
          <button onClick={fetchInspections}
            className="p-2 bg-gray-800 border border-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>
      
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard title="Scheduled" value={stats.scheduled} icon={Calendar} color="#38bdf8" />
        <StatCard title="Completed" value={stats.completed} icon={CheckCircle} color="#10b981" />
        <StatCard title="Overdue" value={stats.overdue} icon={AlertCircle} color="#ef4444" />
        <StatCard title="Pass Rate" value={`${stats.passRate}%`} icon={Activity} color="#a78bfa" sub="Last 30 days" />
      </div>
      
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search by #, template, or zone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg py-2 pl-9 pr-4 text-white text-sm"
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Status</option>
          <option value="scheduled">Scheduled</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="overdue">Overdue</option>
        </select>
        
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-gray-300 text-sm"
        >
          <option value="all">All Types</option>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
        </select>
        
        <button 
          onClick={openCreateModal}
          className="flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm transition-colors"
        >
          <Plus size={14} /> New Inspection
        </button>
      </div>
      
      {/* Inspections Table */}
      <div className="bg-gray-800/60 border border-gray-700/60 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-800 border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Inspection #</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Template / Zone</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Scheduled</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Status</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Result</th>
                <th className="px-4 py-3 text-left text-gray-400 text-xs font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center py-12 text-gray-500"><Loader className="animate-spin mx-auto" /></td></tr>
              ) : filteredInspections.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-12 text-gray-500">No inspections found</td></tr>
              ) : (
                filteredInspections.map((insp) => (
                  <React.Fragment key={insp.id}>
                    <tr className="border-b border-gray-700/50 hover:bg-gray-700/30 cursor-pointer"
                        onClick={() => setExpandedId(expandedId === insp.id ? null : insp.id)}>
                      <td className="px-4 py-3 text-white font-mono text-sm">{insp.inspection_number}</td>
                      <td className="px-4 py-3">
                        <div className="text-white text-sm">{insp.template_name}</div>
                        <div className="text-gray-500 text-xs">{insp.zone_name || insp.zone_type || '—'}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-sm">{insp.scheduled_date}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(insp.status)}`}>
                          {insp.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {insp.overall_result && (
                          <span className={`text-sm font-medium ${getResultColor(insp.overall_result)}`}>
                            {insp.overall_result.toUpperCase()}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button className="p-1 hover:bg-gray-600 rounded text-blue-400"><Eye size={14} /></button>
                          <button className="p-1 hover:bg-gray-600 rounded text-yellow-400"><Edit2 size={14} /></button>
                          <button className="p-1 hover:bg-gray-600 rounded text-red-400"><Trash2 size={14} /></button>
                          {expandedId === insp.id ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </div>
                      </td>
                    </tr>
                    {expandedId === insp.id && (
                      <tr className="bg-gray-800/40">
                        <td colSpan="6" className="px-4 py-4">
                          <div className="space-y-2">
                            <p className="text-gray-300 text-sm"><span className="text-gray-500">Description:</span> {insp.description || 'No description'}</p>
                            <p className="text-gray-300 text-sm"><span className="text-gray-500">Performed by:</span> {insp.performed_by_name || 'Not yet performed'}</p>
                            <p className="text-gray-300 text-sm"><span className="text-gray-500">Performed at:</span> {insp.performed_date || '—'}</p>
                            {insp.findings_summary && <p className="text-gray-300 text-sm"><span className="text-gray-500">Findings:</span> {insp.findings_summary}</p>}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      
      {/* Footer */}
      <div className="text-center py-4">
        <p className="text-gray-500 text-xs">
          Generated by AeroTrick for {company?.name || 'AeroTrick FBO'} · © 2026 AeroTrick. All rights reserved.
        </p>
      </div>
      
      {/* Modal de création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl w-full max-w-md border border-gray-700">
            <div className="p-4 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-white font-semibold">New Inspection</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-gray-400 hover:text-white">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="text-gray-400 text-xs block mb-1">Template *</label>
                <select
                  value={createForm.template_id}
                  onChange={(e) => setCreateForm({...createForm, template_id: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">Select a template...</option>
                  {templates.map(t => (
                    <option key={t.id} value={t.id}>{t.template_code} - {t.template_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Zone</label>
                <select
                  value={createForm.zone_id}
                  onChange={(e) => setCreateForm({...createForm, zone_id: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">Select a zone...</option>
                  {zones.map(z => (
                    <option key={z.id} value={z.id}>{z.zone_code} - {z.zone_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Scheduled Date</label>
                <input
                  type="date"
                  value={createForm.scheduled_date}
                  onChange={(e) => setCreateForm({...createForm, scheduled_date: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs block mb-1">Assign To</label>
                <select
                  value={createForm.assigned_to}
                  onChange={(e) => setCreateForm({...createForm, assigned_to: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white text-sm"
                >
                  <option value="">Unassigned</option>
                  {staff.map(s => (
                    <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end gap-3">
              <button onClick={() => setShowCreateModal(false)} className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm">
                Cancel
              </button>
              <button
                onClick={handleCreateInspection}
                disabled={creating || !createForm.template_id}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white text-sm"
              >
                {creating ? 'Creating...' : 'Create Inspection'}
              </button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default InspectionsView;